/**
 * 自动保存 Hook
 * 职责: 在表单数据变化后自动保存，支持防抖和手动触发
 * 限制: <150行
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { App } from 'antd';
import { debounce } from '../utils/debounce';

interface UseAutoSaveOptions<T> {
  /** 保存函数 - 返回 Promise */
  onSave: (data: T) => Promise<any>;
  /** 防抖延迟时间（毫秒），默认 2000ms */
  delay?: number;
  /** 是否启用自动保存，默认 true */
  enabled?: boolean;
  /** 保存成功回调 */
  onSuccess?: (data: any) => void;
  /** 保存失败回调 */
  onError?: (error: any) => void;
  /** 是否显示保存提示，默认 true */
  showMessage?: boolean;
}

interface AutoSaveState {
  /** 是否正在保存 */
  isSaving: boolean;
  /** 最后保存时间 */
  lastSavedAt: Date | null;
  /** 是否有未保存的更改 */
  hasUnsavedChanges: boolean;
}

/**
 * 自动保存 Hook
 * 
 * @example
 * const { triggerSave, state } = useAutoSave({
 *   onSave: async (data) => await updateSKU(data),
 *   delay: 2000,
 *   enabled: true
 * });
 * 
 * // 在表单值变化时调用
 * const onValuesChange = (changedValues, allValues) => {
 *   triggerSave(allValues);
 * };
 */
export function useAutoSave<T = any>(options: UseAutoSaveOptions<T>) {
  const {
    onSave,
    delay = 2000,
    enabled = true,
    onSuccess,
    onError,
    showMessage = true
  } = options;

  const { message } = App.useApp();
  const [state, setState] = useState<AutoSaveState>({
    isSaving: false,
    lastSavedAt: null,
    hasUnsavedChanges: false
  });

  const saveTimerRef = useRef<NodeJS.Timeout>();
  const lastDataRef = useRef<T>();

  // 实际执行保存的函数
  const executeSave = useCallback(async (data: T) => {
    if (!enabled) return;

    setState(prev => ({ ...prev, isSaving: true }));

    try {
      const result = await onSave(data);

      setState({
        isSaving: false,
        lastSavedAt: new Date(),
        hasUnsavedChanges: false
      });

      if (showMessage) {
        message.success('自动保存成功', 1);
      }

      onSuccess?.(result);
    } catch (error) {
      setState(prev => ({
        ...prev,
        isSaving: false,
        hasUnsavedChanges: true
      }));

      if (showMessage) {
        message.error('自动保存失败');
      }

      onError?.(error);
      console.error('Auto-save failed:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSave, enabled, onSuccess, onError, showMessage]);

  // 防抖保存函数
  const debouncedSave = useRef(
    debounce((data: T) => {
      executeSave(data);
    }, delay)
  ).current;

  // 触发保存（防抖）
  const triggerSave = useCallback((data: T) => {
    if (!enabled) return;

    lastDataRef.current = data;
    setState(prev => ({ ...prev, hasUnsavedChanges: true }));
    debouncedSave(data);
  }, [enabled, debouncedSave]);

  // 立即保存（不防抖）
  const saveNow = useCallback(() => {
    if (!enabled || !lastDataRef.current) return;

    // 取消防抖的保存
    debouncedSave.cancel();
    executeSave(lastDataRef.current);
  }, [enabled, executeSave, debouncedSave]);

  // 组件卸载时清理
  useEffect(() => {
    const currentTimer = saveTimerRef.current;
    return () => {
      if (currentTimer) {
        clearTimeout(currentTimer);
      }
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  return {
    /** 触发自动保存（防抖） */
    triggerSave,
    /** 立即保存（不防抖） */
    saveNow,
    /** 当前状态 */
    state,
    /** 取消待执行的保存 */
    cancel: debouncedSave.cancel
  };
}

